// @ts-ignore
/* eslint-disable */
import { request } from '@umijs/max';
import { supabase, isSupabaseConfigured } from '@/lib/supabaseClient';

/** 获取当前的用户 */
export async function currentUser(options?: { [key: string]: any }) {
  try {
    // 检查 Supabase 配置
    if (!isSupabaseConfigured) {
      throw new Error('Supabase 配置缺失');
    }

    // 获取当前会话
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError || !session?.user) {
      throw new Error('未登录');
    }

    // 获取用户ID（Supabase Auth 的 user.id 是 UUID 字符串）
    const userId = session.user.id;

    // 从 users 表获取用户信息
    const { data: userInfo, error: userError } = await supabase
      .from('users')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (userError || !userInfo) {
      throw new Error('用户信息不存在');
    }

    // 检查是否为管理员
    if (userInfo.role !== 'admin') {
      throw new Error('非管理员用户，无权访问');
    }

    // 返回符合 API.CurrentUser 格式的数据
    return {
      data: {
        name: userInfo.nickname || userInfo.ingamename || session.user.email?.split('@')[0] || 'Admin',
        avatar: undefined,
        userid: userId,
        email: session.user.email || '',
        signature: '',
        title: '管理员',
        group: '',
        tags: [],
        notifyCount: 0,
        unreadCount: 0,
        country: 'China',
        access: 'admin',
        geographic: {
          province: { label: '', key: '' },
          city: { label: '', key: '' },
        },
        address: '',
        phone: '',
      },
    };
  } catch (error: any) {
    throw error;
  }
}

/** 退出登录接口 */
export async function outLogin(options?: { [key: string]: any }) {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
    return { data: {}, success: true };
  } catch (error) {
    console.error('登出失败:', error);
    // 即使出错也返回成功，确保清除本地状态
    return { data: {}, success: true };
  }
}

/** 登录接口 */
export async function login(body: { email: string; password: string }, options?: { [key: string]: any }) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: body.email,
      password: body.password,
    });

    if (error) {
      return {
        status: 'error',
        type: 'account',
        currentAuthority: 'guest',
        error: error.message,
      };
    }

    if (!data.user) {
      return {
        status: 'error',
        type: 'account',
        currentAuthority: 'guest',
        error: '登录失败',
      };
    }

    // 验证用户是否为管理员
    const userId = data.user.id;
    const { data: userInfo, error: userError } = await supabase
      .from('users')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (userError || !userInfo) {
      // 登出，因为用户信息不存在
      await supabase.auth.signOut();
      return {
        status: 'error',
        type: 'account',
        currentAuthority: 'guest',
        error: '用户信息不存在',
      };
    }

    if (userInfo.role !== 'admin') {
      // 登出，因为不是管理员
      await supabase.auth.signOut();
      return {
        status: 'error',
        type: 'account',
        currentAuthority: 'guest',
        error: '非管理员用户，无权访问',
      };
    }

    return {
      status: 'ok',
      type: 'account',
      currentAuthority: 'admin',
    };
  } catch (error: any) {
    return {
      status: 'error',
      type: 'account',
      currentAuthority: 'guest',
      error: error.message || '登录失败',
    };
  }
}

/** 此处后端没有提供注释 GET /api/notices */
export async function getNotices(options?: { [key: string]: any }) {
  return request<API.NoticeIconList>('/api/notices', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 获取规则列表 GET /api/rule */
export async function rule(
  params: {
    // query
    /** 当前的页码 */
    current?: number;
    /** 页面的容量 */
    pageSize?: number;
  },
  options?: { [key: string]: any },
) {
  return request<API.RuleList>('/api/rule', {
    method: 'GET',
    params: {
      ...params,
    },
    ...(options || {}),
  });
}

/** 更新规则 PUT /api/rule */
export async function updateRule(options?: { [key: string]: any }) {
  return request<API.RuleListItem>('/api/rule', {
    method: 'POST',
    data: {
      method: 'update',
      ...(options || {}),
    },
  });
}

/** 新建规则 POST /api/rule */
export async function addRule(options?: { [key: string]: any }) {
  return request<API.RuleListItem>('/api/rule', {
    method: 'POST',
    data: {
      method: 'post',
      ...(options || {}),
    },
  });
}

/** 删除规则 DELETE /api/rule */
export async function removeRule(options?: { [key: string]: any }) {
  return request<Record<string, any>>('/api/rule', {
    method: 'POST',
    data: {
      method: 'delete',
      ...(options || {}),
    },
  });
}
